/**
 * @fileoverview Bill Summary Cards Component
 * @task B.INV.2 - Accounts Payable dashboard cards
 * @theme Polished Corporate - Teal accents, refined shadows
 *
 * Displays summary statistics for bills:
 * - Total Outstanding
 * - Due This Week
 * - Overdue
 * - Paid This Month
 */

import { useEffect, useState } from 'react';
import { cn } from '@lib/utils';
import { useMarketApi } from '../../hooks/useMarketApi';
import { useMarket } from '@contexts/MarketContext';
import type { BillsSummary } from '@lib/demo-data';

interface SummaryCardProps {
  title: string;
  value: string;
  subValue?: string | undefined;
  icon: JSX.Element;
  variant: 'default' | 'warning' | 'danger' | 'success';
  isLoading?: boolean | undefined;
}

const variantStyles = {
  default: {
    card: '',
    icon: 'bg-teal-50 text-teal-600',
  },
  warning: {
    card: '',
    icon: 'bg-amber-50 text-amber-600',
  },
  danger: {
    card: '',
    icon: 'bg-red-50 text-red-600',
  },
  success: {
    card: '',
    icon: 'bg-emerald-50 text-emerald-600',
  },
};

function SummaryCard({
  title,
  value,
  subValue,
  icon,
  variant,
  isLoading,
}: SummaryCardProps): JSX.Element {
  const styles = variantStyles[variant];

  return (
    <div
      className={cn(
        'rounded-xl bg-white p-4 sm:p-5 shadow-card transition-all hover:shadow-card-hover overflow-hidden',
        styles.card
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0 overflow-hidden">
          <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">{title}</p>
          {isLoading ? (
            <div className="mt-2 h-8 w-24 animate-pulse rounded-lg bg-gray-100" />
          ) : (
            <p className="mt-1 text-lg sm:text-2xl font-bold tracking-tight text-gray-900 truncate" title={value}>{value}</p>
          )}
          {subValue && !isLoading && (
            <p className="mt-1 text-xs sm:text-sm text-gray-400 truncate">{subValue}</p>
          )}
        </div>
        <div className={cn('rounded-lg sm:rounded-xl p-2 sm:p-2.5 flex-shrink-0', styles.icon)}>{icon}</div>
      </div>
    </div>
  );
}

interface BillSummaryCardsProps {
  className?: string;
  onRefresh?: () => void;
  isBrazil?: boolean;
  formatCurrency?: (amount: number) => string;
}

/**
 * Bill Summary Cards
 * Displays a grid of summary statistics for the bills module
 */
export function BillSummaryCards({
  className,
  isBrazil: isBrazilProp,
  formatCurrency: formatCurrencyProp,
}: BillSummaryCardsProps): JSX.Element {
  const marketContext = useMarket();
  const { billsApi } = useMarketApi();

  // Use props if provided, otherwise fall back to context
  const isBrazil = isBrazilProp ?? marketContext.isBrazil;
  const formatCurrency = formatCurrencyProp ?? marketContext.formatCurrency;

  const [summary, setSummary] = useState<BillsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchSummary(): Promise<void> {
      try {
        const data = await billsApi.getBillsSummary();
        setSummary(data);
      } catch (error) {
        console.error('Failed to fetch bills summary:', error);
      } finally {
        setIsLoading(false);
      }
    }

    void fetchSummary();
  }, [billsApi]);

  const getBillLabel = (count: number) => {
    if (isBrazil) {
      return count > 1 ? 'faturas' : 'fatura';
    }
    return count > 1 ? 'bills' : 'bill';
  };

  const cards: Omit<SummaryCardProps, 'isLoading'>[] = [
    {
      title: isBrazil ? 'Total Pendente' : 'Total Outstanding',
      value: formatCurrency(summary?.totalOutstanding ?? 0),
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      variant: 'default',
    },
    {
      title: isBrazil ? 'Vence Esta Semana' : 'Due This Week',
      value: formatCurrency(summary?.dueThisWeek ?? 0),
      subValue: summary?.dueThisWeekCount
        ? `${summary.dueThisWeekCount} ${getBillLabel(summary.dueThisWeekCount)}`
        : undefined,
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      variant: 'warning',
    },
    {
      title: isBrazil ? 'Vencido' : 'Overdue',
      value: formatCurrency(summary?.overdue ?? 0),
      subValue: summary?.overdueCount
        ? `${summary.overdueCount} ${getBillLabel(summary.overdueCount)}`
        : undefined,
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      variant: 'danger',
    },
    {
      title: isBrazil ? 'Pago Este Mês' : 'Paid This Month',
      value: formatCurrency(summary?.paidThisMonth ?? 0),
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      variant: 'success',
    },
  ];

  return (
    <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-4', className)}>
      {cards.map((card) => (
        <SummaryCard key={card.title} {...card} isLoading={isLoading} />
      ))}
    </div>
  );
}

export default BillSummaryCards;
